import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Shield, Package, FileCheck, AlertCircle, Building2 } from 'lucide-react';
import Newsletter from '../components/Newsletter';

const MedicalProcurement = () => {
  const capabilities = [
    "Emergency response kits (Naloxone, overdose, trauma, CPR)",
    "Health Canada–approved Class I & II medical devices",
    "PPE and protective equipment",
    "Medical consumables & sterile supplies",
    "Harm-reduction components",
    "Bulk procurement & warehousing",
    "National distribution and logistics support",
    "Quality assurance, documentation & regulatory compliance"
  ];

  const expertise = [
    "Large-scale public health supply programs",
    "Emergency stockpile replenishment",
    "Contract-based procurement",
    "Tender responses & government documentation",
    "Recalls, corrective actions, and QA processes",
    "Supplier auditing & material verification"
  ];

  const emergencyKits = [
    "Naloxone & overdose response kits",
    "CPR masks & face shields",
    "Trauma kits",
    "Harm reduction supplies",
    "Community-based emergency kits",
    "Workplace safety kits"
  ];

  const standards = [
    "Health Canada regulatory conformity",
    "Documented QA & QC protocols",
    "Lot tracking & recall documentation",
    "Multi-stage inspection",
    "Supplier vetting & testing",
    "Regulatory file preparation support"
  ];

  const whyChooseUs = [
    "Proven experience supplying medical products at national scale",
    "Direct relationships with Health Canada–registered manufacturers",
    "End-to-end compliance, documentation, and quality management",
    "High-volume inventory buffers maintained",
    "Urgent emergency procurement timelines met consistently",
    "Reliable, discreet, and professional service delivery"
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative h-[65vh] sm:h-[60vh] md:h-[65vh] lg:h-[70vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-black via-gray-900 to-black">
        {/* Medical themed background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9A55A' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="luxury-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-4 sm:mb-6 leading-tight">
            Medical Procurement &<br />
            Emergency Response Supply
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-white/90 mb-6 sm:mb-8 max-w-4xl mx-auto leading-normal">
            Trusted government-grade sourcing of medical devices, emergency kits, and public health supplies across North America and the Middle East.
          </p>
          <Button 
            asChild
            className="luxury-button bg-[#C9A55A] text-black hover:bg-[#B8941F] font-semibold text-base sm:text-lg px-10 sm:px-14 py-6 sm:py-8 smooth-transition shadow-2xl"
          >
            <Link to="/contact">
              Request Medical Quote
            </Link>
          </Button>
        </div>
      </section>

      {/* Spacing after hero - 80px */}
      <div className="h-20"></div>

      {/* Section 1 - Our Medical Supply Capabilities */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="luxury-heading text-3xl sm:text-4xl md:text-5xl text-white mb-4 sm:mb-6">
              Our Medical Supply Capabilities
            </h2>
          </div>

          {/* Image #2 - Medical Equipment Image */}
          <div className="mb-10 sm:mb-12">
            <div className="max-w-6xl mx-auto">
              <img 
                src="https://customer-assets.emergentagent.com/job_syndicate-consult-1/artifacts/6vd3bj1x_9B88B82B-6296-412F-819C-5A977C64042E.png"
                alt="Medical Supply Equipment"
                className="w-full h-[280px] sm:h-[320px] md:h-[360px] lg:h-[380px] object-cover rounded-lg shadow-2xl"
              />
            </div>
          </div>

          {/* Spacing before capabilities list - 40px */}
          <div className="h-10"></div>

          {/* Content */}
          <div className="max-w-5xl mx-auto mb-8 sm:mb-10">
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed mb-6">
              Syndicate Consultants provides end-to-end medical procurement for government agencies, healthcare authorities, and public safety organizations. Our experience spans large-scale emergency response programs, Health Canada–approved products, and high-volume logistics management across multiple jurisdictions.
            </p>
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
              We deliver reliable, compliant, and scalable medical sourcing solutions built to meet the standards of national healthcare systems and public health authorities.
            </p>
          </div>

          {/* Capabilities List */}
          <Card className="bg-gradient-to-br from-[#C9A55A]/10 to-[#C9A55A]/5 border-[#C9A55A]/20 max-w-5xl mx-auto">
            <CardContent className="p-6 sm:p-8 md:p-10">
              <h3 className="text-xl sm:text-2xl font-semibold text-white mb-6 text-center">Capabilities Include:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {capabilities.map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle className="text-[#C9A55A] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-200 text-sm sm:text-base leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Section 2 - Government & Public Health Expertise */}
      <section className="py-16 sm:py-20 md:py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Title */}
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="luxury-heading text-3xl sm:text-4xl md:text-5xl text-white mb-4 sm:mb-6">
              Government & Public Health Expertise
            </h2>
          </div>

          {/* Intro Paragraphs */}
          <div className="max-w-5xl mx-auto mb-10">
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed mb-6 text-center">
              Syndicate has supported public health authorities and government agencies with high-volume medical procurement, emergency response supply, and compliance-ready documentation.
            </p>
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed text-center">
              This includes working with major Canadian healthcare bodies and following strict procurement, regulatory, and quality standards.
            </p>
          </div>

          {/* Image #3 - Government Building with Ambulance */}
          <div className="mb-10 sm:mb-12">
            <div className="max-w-6xl mx-auto">
              <img 
                src="https://customer-assets.emergentagent.com/job_syndicate-consult/artifacts/1vexb867_B3FC11AB-1B07-4202-B5A8-4AA82D0EF8F7.png"
                alt="Government & Public Health Expertise"
                className="w-full h-[280px] sm:h-[320px] md:h-[360px] lg:h-[380px] object-cover rounded-lg shadow-2xl"
              />
            </div>
          </div>

          {/* Spacing before expertise list - 40px */}
          <div className="h-10"></div>

          {/* Expertise List */}
          <div className="max-w-4xl mx-auto">
            <Card className="bg-gradient-to-br from-[#C9A55A]/10 to-[#C9A55A]/5 border-[#C9A55A]/20">
              <CardContent className="p-6 sm:p-8 md:p-10">
                <h3 className="text-xl sm:text-2xl font-semibold text-[#C9A55A] mb-6 text-center">Expertise Includes:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {expertise.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <Shield className="text-[#C9A55A] flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-200 text-sm sm:text-base leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section 3 - Emergency Response Kits */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Title */}
          <div className="text-center mb-10 sm:mb-12">
            <AlertCircle className="text-[#C9A55A] mx-auto mb-4" size={48} />
            <h2 className="luxury-heading text-3xl sm:text-4xl md:text-5xl text-white mb-4 sm:mb-6">
              Emergency Response Kits
            </h2>
          </div>

          {/* Intro Paragraph */}
          <div className="max-w-5xl mx-auto mb-10 sm:mb-12">
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed text-center">
              Our emergency response kits are used by healthcare authorities, first responders, community organizations, and public safety programs across Canada. These kits include Naloxone for overdose response, CPR masks and barriers, trauma supplies, harm reduction materials, and comprehensive workplace safety equipment — all meeting Health Canada standards and compliance requirements.
            </p>
          </div>

          {/* Image #4 - Emergency Kit Components */}
          <div className="mb-10 sm:mb-12">
            <div className="max-w-6xl mx-auto">
              <img 
                src="https://customer-assets.emergentagent.com/job_syndicate-consult/artifacts/zr7lsb2e_62816E07-6282-4E4E-A91A-1664B8B0D1D3.png"
                alt="Emergency Response Kit Components"
                className="w-full h-[280px] sm:h-[320px] md:h-[360px] lg:h-[380px] object-cover rounded-lg shadow-2xl"
              />
            </div>
          </div>

          {/* Spacing before kit list - 40px */}
          <div className="h-10"></div>

          {/* Kit Types List */}
          <div className="max-w-4xl mx-auto">
            <Card className="bg-gradient-to-br from-[#C9A55A]/10 to-[#C9A55A]/5 border-[#C9A55A]/20">
              <CardContent className="p-6 sm:p-8 md:p-10">
                <h3 className="text-xl sm:text-2xl font-semibold text-white mb-6 text-center">Kit Types We Supply:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {emergencyKits.map((kit, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <Package className="text-[#C9A55A] flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-200 text-sm sm:text-base leading-relaxed">{kit}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section 4 - Quality, Compliance & Regulatory Standards */}
      <section className="py-16 sm:py-20 md:py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Title */}
          <div className="text-center mb-10 sm:mb-12">
            <FileCheck className="text-[#C9A55A] mx-auto mb-4" size={48} />
            <h2 className="luxury-heading text-3xl sm:text-4xl md:text-5xl text-white mb-4 sm:mb-6">
              Quality, Compliance & Regulatory Standards
            </h2>
          </div>

          {/* Intro Paragraph */}
          <div className="max-w-5xl mx-auto mb-10">
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed text-center">
              Every product delivered through Syndicate meets or exceeds government procurement and Health Canada compliance requirements. Our quality assurance protocols include rigorous inspection, lot tracking, regulatory documentation, and supplier verification processes — ensuring complete compliance and traceability for public health authorities.
            </p>
          </div>

          {/* Image #5 - Quality & Compliance Icons */}
          <div className="mb-10 sm:mb-12">
            <div className="max-w-6xl mx-auto">
              <img 
                src="https://customer-assets.emergentagent.com/job_syndicate-consult/artifacts/0hxvhfss_B2057F5A-FE74-4CC9-BB49-9D6C37B72808.png"
                alt="Quality, Compliance & Regulatory Standards"
                className="w-full h-[280px] sm:h-[300px] md:h-[320px] lg:h-[360px] object-cover rounded-lg shadow-2xl"
              />
            </div>
          </div>

          {/* Spacing before standards list - 40px */}
          <div className="h-10"></div>

          {/* Standards List */}
          <Card className="bg-gradient-to-br from-[#C9A55A]/10 to-[#C9A55A]/5 border-[#C9A55A]/30 max-w-4xl mx-auto">
            <CardContent className="p-8 sm:p-12">
              <h3 className="text-2xl sm:text-3xl font-semibold text-white mb-6 sm:mb-8 text-center">Our Standards Include:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {standards.map((standard, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle className="text-[#C9A55A] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-200 text-sm sm:text-base leading-relaxed">{standard}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Section 5 - Why Government Agencies Work With Syndicate */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Title */}
          <div className="text-center mb-12 sm:mb-14">
            <h2 className="luxury-heading text-3xl sm:text-4xl md:text-5xl text-white mb-4 sm:mb-6">
              Why Government Agencies Work With Syndicate
            </h2>
          </div>

          {/* Image #6 - Government Trust Shield */}
          <div className="mb-10 sm:mb-12">
            <div className="max-w-6xl mx-auto">
              <img 
                src="https://customer-assets.emergentagent.com/job_syndicate-consult/artifacts/6f7c90n8_CE00B38E-D6F6-4B65-9AEC-49419A96A4CA.png"
                alt="Why Government Agencies Work With Syndicate"
                className="w-full h-[280px] sm:h-[300px] md:h-[320px] lg:h-[360px] object-contain rounded-lg"
              />
            </div>
          </div>

          {/* Spacing before content - 50px */}
          <div className="h-12"></div>

          {/* Intro Paragraph */}
          <div className="max-w-5xl mx-auto mb-8 sm:mb-10">
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed text-center">
              Government customers choose Syndicate because of our proven track record in supplying medical products at national scale, our direct relationships with Health Canada–registered manufacturers, and our comprehensive approach to compliance, documentation, and quality management from procurement through delivery.
            </p>
          </div>

          {/* Trust Factors List */}
          <Card className="bg-gradient-to-br from-black to-[#1a1a1a] border-[#C9A55A]/30 max-w-5xl mx-auto">
            <CardContent className="p-8 sm:p-12">
              <div className="space-y-4 sm:space-y-6">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-[#C9A55A]/20 rounded-full flex items-center justify-center flex-shrink-0 border border-[#C9A55A]/30">
                      <span className="text-[#C9A55A] font-bold">{idx + 1}</span>
                    </div>
                    <p className="text-gray-200 text-base sm:text-lg leading-relaxed">{reason}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Section 6 - Contact CTA */}
      <section className="relative py-16 sm:py-20 md:py-24 overflow-hidden bg-black">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#C9A55A] rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="luxury-heading text-3xl sm:text-4xl md:text-5xl text-white mb-4 sm:mb-6">
            Work With a Proven Government Medical Supplier
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-8 sm:mb-12 leading-relaxed">
            For urgent medical procurement, emergency supply contracts, or public health program support, contact our medical procurement team.
          </p>
          <Button 
            asChild
            className="luxury-button bg-[#C9A55A] text-black hover:bg-[#B8941F] font-semibold text-base sm:text-lg px-10 sm:px-14 py-6 sm:py-8"
          >
            <Link to="/contact">Request Medical Quote</Link>
          </Button>
        </div>
      </section>

      <Newsletter />
    </div>
  );
};

export default MedicalProcurement;
